import { useCallback, useEffect, useRef, useState } from 'react';

const MIN_CAPTURE_INTERVAL = 3000;
const COUNTDOWN_START = 3;

export const useAutoCapture = ({ isDeviceReady }: { isDeviceReady: boolean }) => {
  const [countdown, setCountdown] = useState(0);
  const [autoCapturing, setAutoCapturing] = useState(false);
  const [shouldCapture, setShouldCapture] = useState(false);
  const lastCaptureTimeRef = useRef<number>(0);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);

  const clearCountdownTimer = useCallback(() => {
    if (countdownTimerRef.current) {
      clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  const startCountdown = useCallback(() => {
    clearCountdownTimer();
    setShouldCapture(false);
    setCountdown(COUNTDOWN_START);
  }, [clearCountdownTimer]);

  const stopCountdown = useCallback(() => {
    clearCountdownTimer();
    setCountdown(0);
    setShouldCapture(false);
  }, [clearCountdownTimer]);

  const toggleAutoCapture = useCallback(() => {
    setAutoCapturing((prev) => {
      const newState = !prev;
      if (newState && isDeviceReady) {
        startCountdown();
      } else {
        stopCountdown();
      }
      return newState;
    });
  }, [isDeviceReady, startCountdown, stopCountdown]);

  const markCaptureTriggered = useCallback(() => {
    setShouldCapture(false);
    if (autoCapturing && isDeviceReady) {
      startCountdown();
    }
  }, [autoCapturing, isDeviceReady, startCountdown]);

  useEffect(() => {
    if (autoCapturing) {
      if (isDeviceReady && countdown === 0) {
        startCountdown();
      } else if (!isDeviceReady) {
        clearCountdownTimer();
        setCountdown(0);
        setShouldCapture(false);
      }
    }
  }, [isDeviceReady, autoCapturing, countdown, startCountdown, clearCountdownTimer]);

  useEffect(() => {
    if (!autoCapturing || !isDeviceReady || countdown === 0) {
      return;
    }

    if (countdown === 1) {
      countdownTimerRef.current = setTimeout(() => {
        const now = Date.now();
        const timeSinceLastCapture = now - lastCaptureTimeRef.current;

        if (timeSinceLastCapture >= MIN_CAPTURE_INTERVAL) {
          setShouldCapture(true);
          lastCaptureTimeRef.current = now;
          setCountdown(0);
        } else {
          startCountdown();
        }
      }, 1000);
    } else {
      countdownTimerRef.current = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }

    return clearCountdownTimer;
  }, [countdown, autoCapturing, isDeviceReady, startCountdown, clearCountdownTimer]);

  return {
    autoCapturing,
    countdown,
    markCaptureTriggered,
    shouldCapture,
    toggleAutoCapture,
  };
};
